/**
 * My Events — CHI Alliance
 * Shows all events the logged-in member has registered for
 */
import { useMemo, useState } from "react";
import { registrationStateLabel } from "@shared/registrationStates";
import { useLocation } from "wouter";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";
import { Calendar, MapPin, Clock, Video, Building2, ArrowRight, Ticket } from "lucide-react";
import { FadeUp, StaggerGroup } from "@/components/ScrollAnimations";
import { trpc } from "@/lib/trpc";
import MyEventsFilters, { type FilterGroup } from "@/components/MyEventsFilters";
import { EVENTS, type CHIEvent } from "@/lib/eventsData";
import { paidEventCatalogRefetchInterval } from "@/lib/eventCatalogFreshness";
import { WAITLIST_REGISTRANT_COPY } from "@shared/registrationWaitlist";
import MyTickets from "@/components/tickets/MyTickets";
import { useMyTicketsBySlug } from "@/hooks/useMyTicketsBySlug";

type DashboardEvent = Pick<CHIEvent, "slug" | "title" | "subtitle" | "date" | "displayDate" | "time" | "type" | "location" | "color" | "seatsRemaining"> & {
  id: string;
  month: string;
  day: string;
  /** The durable attendeeRegistrations state, carried through so the rail can filter on it. */
  registrationState: string;
};

const BANNED_REGISTRANT_COPY_PATTERN = /\b(rejected|denied|refused|failed|not selected)\b/i;

function myEventsRegistrationBadge(state: string) {
  if (state === "registered") return { tone: "registered" as const, label: "✓ Registered" as const };
  if (state === "waitlisted") return { tone: "waitlisted" as const, label: "Waitlisted" as const };
  if (state === "pending_review") return { tone: "pending" as const, label: "Awaiting Approval" as const };
  if (state === "cancelled") return null;
  const label = registrationStateLabel(state);
  return BANNED_REGISTRANT_COPY_PATTERN.test(label)
    ? { tone: "neutral" as const, label: "Registration update" }
    : { tone: "neutral" as const, label };
}

function RegistrationStatusBadge({ state }: { state: string }) {
  const badge = myEventsRegistrationBadge(state);
  if (!badge) return null;
  const palette = {
    registered: { background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.25)", color: "#22C55E" },
    waitlisted: { background: "rgba(245,166,35,0.1)", border: "1px solid rgba(245,166,35,0.25)", color: "#F5A623" },
    pending: { background: "rgba(56,189,248,0.1)", border: "1px solid rgba(56,189,248,0.25)", color: "#38BDF8" },
    neutral: { background: "rgba(148,163,184,0.1)", border: "1px solid rgba(148,163,184,0.25)", color: "#94A3B8" },
  }[badge.tone];
  return (
    <span
      data-testid="my-events-registration-badge"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "0.3rem",
        ...palette,
        borderRadius: "100px",
        padding: "0.15rem 0.6rem",
        fontFamily: "'Space Grotesk', sans-serif",
        fontSize: "0.62rem",
        fontWeight: 600,
        letterSpacing: "0.08em",
      }}
    >
      {badge.label}
    </span>
  );
}

/** Today as yyyy-mm-dd, compared as text against the catalog's ISO dates. */
function todayIso() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

export default function MyEvents() {
  const { isLoggedIn, user } = useAuth();
  const [, navigate] = useLocation();
  // CHI-29: one myTickets() call grouped by slug (no N+1); admins never get the button.
  const isAdminViewer = user?.role === "admin";
  const myTicketsBySlug = useMyTicketsBySlug(isLoggedIn && !isAdminViewer);
  const myRegistrationsQuery = trpc.events.myRegistrations.useQuery(undefined, { enabled: isLoggedIn, retry: false });
  const publicCatalogQuery = trpc.events.publicCatalog.useQuery(undefined, {
    enabled: isLoggedIn,
    retry: false,
    refetchInterval: paidEventCatalogRefetchInterval,
    refetchOnWindowFocus: true,
  });

  const serverRegisteredEvents = useMemo<DashboardEvent[]>(() => {
    const catalog = new Map<string, Pick<CHIEvent, "slug" | "title" | "subtitle" | "date" | "displayDate" | "time" | "type" | "location" | "color" | "seatsRemaining">>();
    EVENTS.forEach(event => catalog.set(event.slug, event));
    publicCatalogQuery.data?.forEach(event => catalog.set(event.slug, event));

    /*
     * Every registration state is loaded, not just confirmed ones.
     *
     * This list was hard-filtered to `registered`, so a member who was waitlisted
     * or whose registration was cancelled had no way to see it anywhere on the
     * site — the record existed and was invisible. The rail defaults to confirmed,
     * so the page still opens on the same thing it always did.
     */
    /*
     * CHI-29: a non-special paid_internal buyer has webhook-issued tickets but no
     * registration row (the ticket service never writes one), so without this they had
     * no tile and no My Ticket(s) button. Add a "registered" tile for each slug with
     * active tickets and no registration of any state. A non-registered registration
     * row still wins (its tile shows that state and no button, as before).
     */
    const registrations = myRegistrationsQuery.data ?? [];
    const registeredSlugs = new Set(registrations.map(registration => registration.eventSlug));
    const ticketOnly = Array.from(myTicketsBySlug.bySlug.keys())
      .filter(slug => !registeredSlugs.has(slug))
      .map(slug => ({ eventSlug: slug, registrationState: "registered" as const }));
    return [...registrations, ...ticketOnly]
      .map(registration => {
        const event = catalog.get(registration.eventSlug);
        if (!event) {
          return {
            id: registration.eventSlug,
            slug: registration.eventSlug,
            title: "CHI Alliance event",
            subtitle: registration.registrationState === "registered" ? "Registration confirmed" : registrationStateLabel(registration.registrationState),
            date: "",
            displayDate: "Date to be announced",
            time: "Details coming soon",
            type: "virtual" as const,
            location: "Event details coming soon",
            color: "#E91E8C",
            month: "TBD",
            day: "—",
            registrationState: registration.registrationState,
          };
        }

        const date = new Date(`${event.date}T12:00:00`);
        return {
          ...event,
          id: registration.eventSlug,
          month: Number.isNaN(date.valueOf()) ? "TBD" : new Intl.DateTimeFormat("en-US", { month: "short" }).format(date),
          day: Number.isNaN(date.valueOf()) ? "—" : new Intl.DateTimeFormat("en-US", { day: "2-digit" }).format(date),
          registrationState: registration.registrationState,
        };
      })
      /*
       * Soonest first, then further out.
       *
       * There was no sort at all, so the list came back in registration order —
       * whatever sequence someone happened to sign up in. On a page whose purpose
       * is "what am I attending next", that is the one order that answers nothing.
       *
       * Undated events sort last rather than first: `date` is "" for a
       * registration whose event is missing from the catalog, and an empty string
       * compares below every real date, which would put "Date to be announced" at
       * the top. Sorted as strings because the dates are ISO yyyy-mm-dd, where
       * lexical and chronological order are the same — no Date parsing, so a
       * malformed value cannot become an Invalid Date and scramble the comparison.
       */
      .sort((left, right) => {
        if (!left.date && !right.date) return left.title.localeCompare(right.title);
        if (!left.date) return 1;
        if (!right.date) return -1;
        return left.date.localeCompare(right.date) || left.time.localeCompare(right.time);
      });
  }, [myRegistrationsQuery.data, publicCatalogQuery.data, myTicketsBySlug.bySlug]);

  const [status, setStatus] = useState("registered");
  const [timing, setTiming] = useState("upcoming");
  const [format, setFormat] = useState("all");
  const [month, setMonth] = useState("all");

  const today = todayIso();

  /** The predicates, one per group, so counting and filtering cannot diverge. */
  const matches = useMemo(() => ({
    status: (event: DashboardEvent, value: string) => value === "all" || event.registrationState === value,
    // An undated registration counts as upcoming: it has not happened yet, and
    // burying it under "past" is how a member misses an event we never dated.
    timing: (event: DashboardEvent, value: string) => (
      value === "all"
        ? true
        : value === "past"
          ? Boolean(event.date) && event.date < today
          : !event.date || event.date >= today
    ),
    format: (event: DashboardEvent, value: string) => value === "all" || event.type === value,
    month: (event: DashboardEvent, value: string) => value === "all" || event.month === value,
  }), [today]);

  const visibleEvents = useMemo(() => serverRegisteredEvents.filter(event => (
    matches.status(event, status)
    && matches.timing(event, timing)
    && matches.format(event, format)
    && matches.month(event, month)
  )), [serverRegisteredEvents, matches, status, timing, format, month]);

  /**
   * The count a group's option would yield.
   *
   * Every other group's filter is applied, but not this one's — so the numbers
   * describe what switching to that option gives you, rather than what is on
   * screen right now, which would show zero for everything unselected.
   */
  const countFor = (group: "status" | "timing" | "format" | "month", value: string) =>
    serverRegisteredEvents.filter(event => (
      (group === "status" || matches.status(event, status))
      && (group === "timing" || matches.timing(event, timing))
      && (group === "format" || matches.format(event, format))
      && (group === "month" || matches.month(event, month))
      && matches[group](event, value)
    )).length;

  /** Months the member actually has events in, in calendar order. */
  const monthOptions = useMemo(() => {
    const seen: string[] = [];
    serverRegisteredEvents.forEach(event => {
      if (event.month && event.month !== "TBD" && !seen.includes(event.month)) seen.push(event.month);
    });
    return seen;
  }, [serverRegisteredEvents]);

  const filterGroups: FilterGroup[] = [
    {
      key: "status",
      label: "Registration",
      value: status,
      onChange: setStatus,
      options: [
        { value: "registered", label: "Registered", count: countFor("status", "registered") },
        { value: "pending_review", label: registrationStateLabel("pending_review"), count: countFor("status", "pending_review") },
        { value: "waitlisted", label: registrationStateLabel("waitlisted"), count: countFor("status", "waitlisted") },
        { value: "cancelled", label: "Cancelled", count: countFor("status", "cancelled") },
        { value: "all", label: "Any status", count: countFor("status", "all") },
      ],
    },
    {
      key: "timing",
      label: "When",
      value: timing,
      onChange: setTiming,
      options: [
        { value: "upcoming", label: "Upcoming", count: countFor("timing", "upcoming") },
        { value: "past", label: "Past", count: countFor("timing", "past") },
        { value: "all", label: "Any date", count: countFor("timing", "all") },
      ],
    },
    {
      key: "format",
      label: "Format",
      value: format,
      onChange: setFormat,
      options: [
        { value: "all", label: "Any format", count: countFor("format", "all") },
        { value: "virtual", label: "Virtual", count: countFor("format", "virtual") },
        { value: "in-person", label: "In person", count: countFor("format", "in-person") },
      ],
    },
    ...(monthOptions.length > 1 ? [{
      key: "month",
      label: "Month",
      value: month,
      onChange: setMonth,
      options: [
        { value: "all", label: "Any month", count: countFor("month", "all") },
        ...monthOptions.map(value => ({ value, label: value, count: countFor("month", value) })),
      ],
    }] : []),
  ];

  const activeFilterCount = [
    status !== "registered",
    timing !== "upcoming",
    format !== "all",
    month !== "all",
  ].filter(Boolean).length;

  const clearFilters = () => {
    setStatus("registered");
    setTiming("upcoming");
    setFormat("all");
    setMonth("all");
  };

  // Show guest view if not logged in
  if (!isLoggedIn) {
    return (
      <div style={{ background: "#080C18", minHeight: "100vh" }}>
        <Navigation />
        <section style={{ paddingTop: "clamp(96px, 14vw, 128px)", paddingBottom: "5rem", display: "flex", alignItems: "center", justifyContent: "center", minHeight: "80vh" }}>
          <div style={{ textAlign: "center", maxWidth: "480px", padding: "0 1.5rem" }}>
            <div style={{ width: 72, height: 72, borderRadius: "1.25rem", background: "rgba(233,30,140,0.12)", border: "1px solid rgba(233,30,140,0.3)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 1.5rem" }}>
              <Ticket size={32} style={{ color: "#E91E8C" }} />
            </div>
            <h1 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "2.5rem", color: "white", marginBottom: "1rem", lineHeight: 1.1 }}>Your Events</h1>
            <p style={{ color: "#94A3B8", fontSize: "1rem", lineHeight: 1.75, fontFamily: "'Inter', sans-serif", marginBottom: "2rem" }}>
              Sign in or create a free account to view and manage your registered events.
            </p>
            <button
              onClick={() => navigate("/login")}
              style={{ display: "inline-flex", alignItems: "center", gap: "0.625rem", padding: "0.875rem 2rem", background: "linear-gradient(135deg, #E91E8C, #C2185B)", border: "none", borderRadius: "0.625rem", color: "white", fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "0.9rem", letterSpacing: "0.04em", textTransform: "uppercase", cursor: "pointer", boxShadow: "0 8px 24px rgba(233,30,140,0.35)" }}
            >
              Sign In to View Events <ArrowRight size={16} />
            </button>
            <div style={{ marginTop: "1.25rem" }}>
              <a href="/calendar-of-events" style={{ color: "#64748B", fontSize: "0.875rem", fontFamily: "'Inter', sans-serif", textDecoration: "underline" }}>Browse upcoming events →</a>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  if (myRegistrationsQuery.isLoading || publicCatalogQuery.isLoading) {
    return (
      <div style={{ background: "#080C18", minHeight: "100vh" }}>
        <Navigation />
        <section style={{ paddingTop: "clamp(96px, 14vw, 128px)", minHeight: "80vh", display: "grid", placeItems: "center", color: "#94A3B8", fontFamily: "'Inter', sans-serif" }}>Loading your registered events…</section>
        <Footer />
      </div>
    );
  }

  return (
    <div style={{ background: "#080C18", minHeight: "100vh" }}>
      <Navigation />

      {/* ── HERO ── */}
      <section
        style={{
          paddingTop: "120px",
          paddingBottom: "3rem",
          background: "linear-gradient(180deg, rgba(233,30,140,0.08) 0%, transparent 100%)",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        <FadeUp duration={700}>
        <div className="container">
          <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "0.5rem" }}>
            <img
              src="/media/brand/chi-flower-symbol.png"
              alt="CHI Alliance"
              style={{ width: 48, height: 48, filter: "drop-shadow(0 0 14px rgba(233,30,140,0.5))", flexShrink: 0 }}
            />
            <div>
              <div
                style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: "#E91E8C",
                  marginBottom: "0.2rem",
                }}
              >
                Member Dashboard
              </div>
              <h1
                style={{
                  fontFamily: "'Bebas Neue', sans-serif",
                  fontSize: "clamp(2rem, 4vw, 3rem)",
                  color: "white",
                  letterSpacing: "0.02em",
                  lineHeight: 1,
                }}
              >
                My Events
              </h1>
            </div>
          </div>
          <p style={{ color: "#64748B", fontFamily: "'Inter', sans-serif", fontSize: "0.9rem", marginTop: "0.75rem" }}>
            {visibleEvents.length === 0
              ? "You haven't registered for any events yet."
              : `${visibleEvents.length} event${visibleEvents.length !== 1 ? "s" : ""} match your filters.`}
          </p>
        </div>
        </FadeUp>
      </section>

      {/* ── EVENT LIST ── */}
      <section style={{ padding: "3rem 0 6rem" }}>
        <div className="container my-events-layout">
          <MyEventsFilters
            groups={filterGroups}
            activeCount={activeFilterCount}
            onClear={clearFilters}
            resultCount={visibleEvents.length}
          />
          <div className="my-events-layout__list">
          {serverRegisteredEvents.length === 0 ? (
            /* Empty state */
            <div
              style={{
                textAlign: "center",
                padding: "5rem 2rem",
                background: "rgba(255,255,255,0.02)",
                border: "1px dashed rgba(255,255,255,0.1)",
                borderRadius: "1.5rem",
              }}
            >
              <div
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: "50%",
                  background: "rgba(233,30,140,0.1)",
                  border: "1px solid rgba(233,30,140,0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 1.5rem",
                  color: "#E91E8C",
                }}
              >
                <Calendar size={28} />
              </div>
              <h2
                style={{
                  fontFamily: "'Bebas Neue', sans-serif",
                  fontSize: "1.75rem",
                  color: "white",
                  marginBottom: "0.75rem",
                }}
              >
                No Events Yet
              </h2>
              <p style={{ color: "#64748B", fontFamily: "'Inter', sans-serif", fontSize: "0.9rem", marginBottom: "2rem", maxWidth: 400, margin: "0 auto 2rem" }}>
                Browse our upcoming events and register to start building your CHI Alliance experience.
              </p>
              <a
                href="/calendar-of-events"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  background: "linear-gradient(135deg, #E91E8C, #F5A623)",
                  color: "white",
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  padding: "0.75rem 1.5rem",
                  borderRadius: "0.5rem",
                  textDecoration: "none",
                  letterSpacing: "0.04em",
                }}
              >
                Browse Events <ArrowRight size={15} />
              </a>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
              {visibleEvents.map((event) => (
                <div
                  key={event.id}
                  style={{
                    background: "rgba(255,255,255,0.03)",
                    border: `1px solid ${event.color}25`,
                    borderRadius: "1.25rem",
                    overflow: "hidden",
                    transition: "border-color 0.25s ease, box-shadow 0.25s ease",
                  }}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLElement).style.borderColor = `${event.color}50`;
                    (e.currentTarget as HTMLElement).style.boxShadow = `0 8px 40px ${event.color}18`;
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLElement).style.borderColor = `${event.color}25`;
                    (e.currentTarget as HTMLElement).style.boxShadow = "none";
                  }}
                >
                  {/* Top accent bar */}
                  <div style={{ height: 4, background: `linear-gradient(90deg, ${event.color}, ${event.color}60)` }} />

                  <div style={{ padding: "1.5rem 2rem", display: "flex", alignItems: "center", gap: "1.75rem", flexWrap: "wrap" }}>
                    {/* Calendar badge */}
                    <div
                      style={{
                        flexShrink: 0,
                        width: 64,
                        borderRadius: "0.75rem",
                        overflow: "hidden",
                        border: `1px solid ${event.color}30`,
                        boxShadow: `0 4px 20px ${event.color}20`,
                      }}
                    >
                      <div
                        style={{
                          background: `linear-gradient(135deg, ${event.color}, ${event.color}99)`,
                          padding: "0.3rem 0",
                          textAlign: "center",
                          fontFamily: "'Space Grotesk', sans-serif",
                          fontSize: "0.6rem",
                          fontWeight: 700,
                          letterSpacing: "0.12em",
                          textTransform: "uppercase",
                          color: "white",
                        }}
                      >
                        {event.month}
                      </div>
                      <div
                        style={{
                          background: `linear-gradient(180deg, ${event.color}18, rgba(8,12,24,0.9))`,
                          padding: "0.4rem 0",
                          textAlign: "center",
                          fontFamily: "'Bebas Neue', sans-serif",
                          fontSize: "1.75rem",
                          color: "white",
                          lineHeight: 1,
                          textShadow: `0 0 12px ${event.color}60`,
                        }}
                      >
                        {event.day}
                      </div>
                    </div>

                    {/* Event info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.4rem", flexWrap: "wrap" }}>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "0.3rem",
                            background: `${event.color}15`,
                            border: `1px solid ${event.color}30`,
                            borderRadius: "100px",
                            padding: "0.15rem 0.6rem",
                            fontFamily: "'Space Grotesk', sans-serif",
                            fontSize: "0.62rem",
                            fontWeight: 600,
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                            color: event.color,
                          }}
                        >
                          {event.type === "virtual" ? <Video size={9} /> : <Building2 size={9} />}
                          {event.type}
                        </span>
                        <RegistrationStatusBadge state={event.registrationState} />
                      </div>
                      <h3
                        style={{
                          fontFamily: "'Bebas Neue', sans-serif",
                          fontSize: "1.3rem",
                          color: "white",
                          marginBottom: "0.4rem",
                          letterSpacing: "0.02em",
                        }}
                      >
                        {event.title}
                      </h3>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem" }}>
                        <span style={{ display: "flex", alignItems: "center", gap: "0.35rem", color: "#64748B", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.78rem" }}>
                          <Clock size={12} style={{ color: event.color }} /> {event.date}
                        </span>
                        <span style={{ display: "flex", alignItems: "center", gap: "0.35rem", color: "#64748B", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.78rem" }}>
                          <MapPin size={12} style={{ color: event.color }} /> {event.location}
                        </span>
                      </div>
                      {event.registrationState === "waitlisted" && (
                        <p
                          data-testid="my-events-waitlist-copy"
                          style={{ margin: "0.65rem 0 0", color: "#CBD5E1", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.55 }}
                        >
                          {WAITLIST_REGISTRANT_COPY}
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.625rem", flexShrink: 0 }}>
                      {event.registrationState === "registered" && event.type === "in-person" && <a
                        href={`/my-events/ticket/${event.slug}`}
                        className="chi-btn-primary"
                        style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, minHeight: 44 }}
                      ><Ticket size={16} /> View ticket</a>}
                      <a
                        href={`/events/${event.slug}`}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "0.4rem",
                          background: `${event.color}18`,
                          border: `1px solid ${event.color}35`,
                          color: event.color,
                          fontFamily: "'Space Grotesk', sans-serif",
                          fontWeight: 600,
                          fontSize: "0.78rem",
                          padding: "0.5rem 1rem",
                          borderRadius: "0.5rem",
                          textDecoration: "none",
                          whiteSpace: "nowrap",
                          transition: "background 0.2s ease",
                        }}
                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = `${event.color}30`; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = `${event.color}18`; }}
                      >
                        View Details <ArrowRight size={13} />
                      </a>
                      {event.registrationState === "registered" && !isAdminViewer && (myTicketsBySlug.bySlug.get(event.slug)?.length ?? 0) > 0 && (
                        <a
                          href={`/my-events/${encodeURIComponent(event.slug)}/tickets`}
                          data-testid="my-event-tickets-button"
                          style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "0.4rem", background: "rgba(34,197,94,0.15)", border: "1px solid rgba(34,197,94,0.4)", color: "#4ADE80", fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600, fontSize: "0.78rem", padding: "0.5rem 1rem", borderRadius: "0.5rem", textDecoration: "none", whiteSpace: "nowrap" }}
                        >
                          My Ticket(s)
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {/* Browse more CTA */}
              <div style={{ textAlign: "center", marginTop: "1.5rem" }}>
                <a
                  href="/calendar-of-events"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    color: "#CBD5E1",
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontWeight: 500,
                    fontSize: "0.85rem",
                    padding: "0.75rem 1.75rem",
                    borderRadius: "0.5rem",
                    textDecoration: "none",
                    transition: "background 0.2s ease, color 0.2s ease",
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.08)"; (e.currentTarget as HTMLElement).style.color = "white"; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.04)"; (e.currentTarget as HTMLElement).style.color = "#CBD5E1"; }}
                >
                  Browse More Events <ArrowRight size={15} />
                </a>
              </div>
            </div>
          )}
          </div>
        </div>
      </section>

      <style>{`
        .my-events-layout{display:grid;grid-template-columns:16.5rem minmax(0,1fr);gap:1.5rem;align-items:start}
        .my-events-layout__list{min-width:0}
        @media(max-width:1000px){.my-events-layout{grid-template-columns:minmax(0,1fr)}}
      `}</style>

      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 1.5rem 3rem" }}>
        <MyTickets
          authenticatedViewer={isLoggedIn}
          titleForSlug={slug => publicCatalogQuery.data?.find(e => e.slug === slug)?.title ?? EVENTS.find(e => e.slug === slug)?.title}
          detailsForSlug={slug => {
            const event = publicCatalogQuery.data?.find(e => e.slug === slug) ?? EVENTS.find(e => e.slug === slug);
            return event ? { date: event.displayDate || event.date, time: event.time, venue: event.location } : undefined;
          }}
        />
      </div>

      <Footer />
    </div>
  );
}
